import { useState, MouseEvent, ChangeEvent } from 'react'
import type { Book, FieldVisibility } from '@/types'
import { formatAuthors, formatCategories } from '../../formattingUtils'
import { getFieldConfigArray } from '../../constants/visibilityConfig'
import VisibilityConfigDropdown from '../VisibilityConfigDropdown'

interface BookWithProfile extends Book {
    profiles?: {
        full_name?: string
    }
}

interface FullCardsViewProps {
    books: BookWithProfile[]
    selectedIds: Set<string>
    toggleBookSelection: (e: MouseEvent | ChangeEvent, bookId: string) => void
    onBookClick: (book: BookWithProfile) => void
    initiateDelete: (e: MouseEvent, bookId: string) => void
    isBiblioverso: boolean
    isSharedView: boolean
    deletingId: string | null
    visibility: FieldVisibility
    toggleField: (field: keyof FieldVisibility) => void
    resetToDefaults: () => void
    allowedFields: string[] | null
}

export default function FullCardsView({
    books,
    selectedIds,
    toggleBookSelection,
    onBookClick,
    initiateDelete,
    isBiblioverso,
    isSharedView,
    deletingId,
    visibility,
    toggleField,
    resetToDefaults,
    allowedFields,
}: Readonly<FullCardsViewProps>) {
    const [showConfig, setShowConfig] = useState(false)

    const fieldConfig = getFieldConfigArray(visibility)

    return (
        <>
            {/* Global Configuration Button */}
            <div className="view-config-toolbar">
                <button className="view-config-btn" onClick={() => setShowConfig(!showConfig)} title="Personalizar campos">
                    ⚙️ Personalizar campos
                </button>

                {showConfig && (
                    <VisibilityConfigDropdown
                        fields={fieldConfig}
                        onToggle={toggleField}
                        onClose={() => setShowConfig(false)}
                        isSharedView={isSharedView}
                        allowedFields={allowedFields}
                        position="bottom-right"
                        onReset={resetToDefaults}
                    />
                )}
            </div>

            <div className="books-grid animate-fade-in">
                {books.map((book, index) => {
                    const isSelected = selectedIds.has(book.id)
                    const ownerName = book.profiles?.full_name || 'Mi Biblioteca'

                    return (
                        <article
                            key={book.id}
                            className={`book-card animate-fade-in-up stagger-${Math.min(index + 1, 5)} clickable ${isSelected ? 'is-selected' : ''}`}
                            onClick={() => onBookClick(book)}
                            role="button"
                            data-testid={`book-card-${book.title}`}
                        >
                            <label className="book-checkbox-wrapper" onClick={(e) => e.stopPropagation()}>
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => toggleBookSelection(e, book.id)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="book-checkbox-input"
                                />
                                <span className="book-checkbox"></span>
                            </label>

                            {isBiblioverso && (
                                <div
                                    className="book-owner-badge"
                                    style={{
                                        position: 'absolute',
                                        top: '10px',
                                        right: '10px',
                                        background: 'rgba(0,0,0,0.6)',
                                        color: 'white',
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        fontSize: '0.75rem',
                                    }}
                                >
                                    {ownerName === 'Mi Biblioteca' ? '👤 Yo' : `👩‍🚀 ${ownerName}`}
                                </div>
                            )}

                            <div className="book-cover">
                                <span className="book-emoji-large">📖</span>
                            </div>

                            <div className="book-content">
                                {/* Title - always visible */}
                                <h3 className="book-title">{book.title}</h3>

                                {/* Author */}
                                {visibility.author && book.author && (
                                    <p className="book-author">
                                        <span className="author-icon">✍️</span>
                                        {formatAuthors(book.author)}
                                    </p>
                                )}

                                {/* Series */}
                                {visibility.series && book.series && (
                                    <p className="book-series">
                                        📚 {book.series}
                                        {visibility.series_number && !!book.series_number && ` #${book.series_number}`}
                                    </p>
                                )}

                                {/* Notes */}
                                {visibility.notes && book.notes && <p className="book-notes">{book.notes}</p>}

                                <div className="book-meta">
                                    {/* Date added */}
                                    {visibility.created_at && (
                                        <span className="book-date">
                                            📅{' '}
                                            {new Date(book.created_at).toLocaleDateString('es-ES', {
                                                day: 'numeric',
                                                month: 'short',
                                                year: 'numeric',
                                            })}
                                        </span>
                                    )}

                                    {/* Progress */}
                                    {visibility.progress && book.progress && <span className="book-status-badge">{book.progress}</span>}

                                    {/* Status */}
                                    {visibility.status && book.status && <span className="book-status-badge status-badge">{book.status}</span>}
                                </div>

                                <div className="book-extra-details">
                                    {/* Publisher */}
                                    {visibility.publisher && book.publisher && <span className="detail-tag">🏢 {book.publisher}</span>}

                                    {/* Format */}
                                    {visibility.format && book.format && <span className="detail-tag">📦 {book.format}</span>}

                                    {/* Location */}
                                    {visibility.location && book.location && <span className="detail-tag">📍 {book.location}</span>}

                                    {/* Category */}
                                    {visibility.category && book.category && Array.isArray(book.category) && book.category.length > 0 && (
                                        <span className="detail-tag">🏷️ {formatCategories(book.category)}</span>
                                    )}

                                    {/* Language */}
                                    {visibility.language && book.language && <span className="detail-tag">🌐 {book.language}</span>}

                                    {/* Page count */}
                                    {visibility.page_count && !!book.page_count && <span className="detail-tag">📄 {book.page_count} pág.</span>}

                                    {/* Synopsis */}
                                    {visibility.synopsis && book.synopsis && (
                                        <p
                                            className="book-synopsis-preview"
                                            style={{
                                                fontSize: '0.8rem',
                                                color: '#888',
                                                marginTop: '0.5rem',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {book.synopsis}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Delete button (only for own books) */}
                            {!isBiblioverso && !isSharedView && (
                                <button
                                    onClick={(e) => initiateDelete(e, book.id)}
                                    disabled={deletingId === book.id}
                                    className="book-delete"
                                    aria-label="Eliminar libro"
                                    data-testid="delete-book-button"
                                >
                                    {deletingId === book.id ? <span className="spinner small"></span> : <span className="delete-icon">🗑️</span>}
                                </button>
                            )}
                        </article>
                    )
                })}
            </div>
        </>
    )
}
